import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router"
import type { Component } from "vue"
import HomeDashboard from "@/views/HomeDashboard.vue"
import MerchantGroupDashboard from "@/views/merchantGroup/MerchantGroupDashboard.vue"
import PortalLogIn from "@/views/PortalLogIn.vue"
import MerchantGroupLogin from "@/views/merchantGroup/MerchantGroupLogin.vue"
import PortalLayout from "@/layouts/PortalLayout.vue"
import MerchantGroupLayout from "@/layouts/MerchantGroupLayout.vue"
import PortalSettings from "@/views/PortalSettings.vue"
import PortalHandleLoginLink from "@/views/PortalHandleLoginLink.vue"
import ReportsTransactions from "@/views/ReportsTransactions.vue"
import ReportsTopProducts from "@/views/ReportsTopProducts.vue"
import ReportsLayout from "@/layouts/ReportsLayout.vue"
import ReportsProductSales from "@/views/ReportsProductSales.vue"
import ReportsOverview from "@/views/ReportsOverview.vue"
import ReportsGratuity from "@/views/ReportsGratuity.vue"
import ProductsIndex from "@/views/ProductsIndex.vue"
import CategoriesIndex from "@/views/CategoriesIndex.vue"
import ProductEdit from "@/views/ProductEdit.vue"
import ProductCreate from "@/views/ProductCreate.vue"
import UsersIndex from "@/views/UsersIndex.vue"
import UserCreate from "@/views/UserCreate.vue"
import UserEdit from "@/views/UserEdit.vue"
import CategoryEdit from "@/views/CategoryEdit.vue"
import CategoryCreate from "@/views/CategoryCreate.vue"
import PasswordResetRequest from "@/views/PasswordResetRequest.vue"
import CashiersIndex from "@/views/CashiersIndex.vue"
import CashierEdit from "@/views/CashierEdit.vue"
import CashierCreate from "@/views/CashierCreate.vue"
import UserPasswordChange from "@/views/UserPasswordChange.vue"

import OrdersIndex from "@/views/OrdersIndex.vue"
import OrderDetail from "@/views/OrderDetail.vue"
import TransactionDetail from "@/views/TransactionDetail.vue"
import ZReportDetail from "@/views/ZReportDetail.vue"
import ZReportsIndex from "@/views/ZReportsIndex.vue"

import {
  IconOrdersIndex,
  IconProductSalesReport,
  IconHome,
  IconCashiersIndex,
  IconTransactionReport,
  IconTransactionsIndex,
  IconProductsIndex,
  IconCategoriesIndex,
  IconSettings,
  IconTopProductsReport,
  IconImportIndex,
  IconUsers,
  IconSalesOverview,
  IconGratuityReport,
  IconTerminal,
  IconZReportsIndex,
} from "../icons"

import ImportIndex from "@/views/ProductImportIndex.vue"
import ImportDetail from "@/views/ProductImportDetail.vue"
import MerchantGroupUserResetPassword from "@/views/merchantGroup/MerchantGroupUserResetPassword.vue"
import MerchantGroupUserEdit from "@/views/merchantGroup/MerchantGroupUserEdit.vue"
import MerchantGroupUserCreate from "@/views/merchantGroup/MerchantGroupUserCreate.vue"
import MerchantGroupMerchantDetail from "@/views/merchantGroup/MerchantGroupMerchantDetail.vue"
import MerchantGroupTransactionsReport from "@/views/merchantGroup/MerchantGroupTransactionsReport.vue"

import AdvancePlus from "@/views/AdvancePlus.vue"
import MerchantGroupUsersIndex from "@/views/merchantGroup/MerchantGroupUsersIndex.vue"
import MerchantGroupTerminalsIndex from "@/views/merchantGroup/MerchantGroupTerminalsIndex.vue"
import PortalHandleMerchantGroupLoginLink from "@/views/merchantGroup/PortalHandleMerchantGroupLoginLink.vue"
import type { RouteNamedMap } from "./RouteNamedMap"
import DonationDetail from "@/views/DonationDetail.vue"
import TransactionsIndex from "@/views/TransactionsIndex.vue"
import HistoryLayout from "@/layouts/HistoryLayout.vue"
import UserResetPassword from "@/views/UserResetPassword.vue"
import MerchantGroupChangePassword from "@/views/merchantGroup/MerchantGroupChangePassword.vue"

const { setPageTitle } = usePageTitle()
const { isLoggedIn } = useAuth()
const { isLoggedInMerchantGroup } = useMerchantGroupAuth()

declare module "vue-router" {
  interface RouteMeta {
    auth?: boolean
    merchantGroupAuth?: boolean
    pageTitle?: string
    icon?: Component
  }
  interface TypesConfig {
    RouteNamedMap: RouteNamedMap
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: "/merchant-group",
    component: MerchantGroupLayout,
    meta: {
      merchantGroupAuth: true,
    },
    children: [
      {
        path: "",
        name: "merchantGroupDashboard",
        component: MerchantGroupDashboard,
        meta: {
          pageTitle: "Dashboard",
          icon: IconHome,
        },
        children: [
          {
            path: "merchants/:schemaName",
            name: "merchantGroupMerchantDetail",
            component: MerchantGroupMerchantDetail,
            props: true,
            meta: {
              pageTitle: "Merchants",
            },
          },
        ],
      },
      {
        path: "terminals",
        name: "merchantGroupTerminalsIndex",
        component: MerchantGroupTerminalsIndex,
        meta: {
          pageTitle: "Terminals",
          icon: IconTerminal,
        },
      },
      {
        path: "reports/transactions",
        name: "merchantGroupTransactionsReport",
        component: MerchantGroupTransactionsReport,
        meta: {
          pageTitle: "Transaction Report",
          icon: IconTransactionReport,
        },
      },
      {
        path: "users",
        name: "merchantGroupUsersIndex",
        component: MerchantGroupUsersIndex,
        meta: {
          pageTitle: "Users",
          icon: IconUsers,
        },
      },
      {
        path: "users/:userId/edit",
        name: "merchantGroupUserEdit",
        component: MerchantGroupUserEdit,
        meta: {
          pageTitle: "Edit User",
        },
        props: true,
      },
      {
        path: "users/new",
        name: "merchantGroupUserCreate",
        component: MerchantGroupUserCreate,
        meta: {
          pageTitle: "Create User",
        },
      },
      {
        path: "change-password",
        name: "changePasswordMerchantGroup",
        component: MerchantGroupChangePassword,
        meta: {
          pageTitle: "Change Password",
        },
      },
    ],
  },
  {
    path: "/",
    component: PortalLayout,
    meta: {
      auth: true,
    },
    children: [
      {
        path: "",
        name: "dashboard",
        component: HomeDashboard,
        meta: {
          pageTitle: "Dashboard",
          icon: IconHome,
        },
      },
      {
        path: "advance-plus",
        name: "advancePlus",
        component: AdvancePlus,
        meta: {
          pageTitle: "Advance+",
        },
      },
      {
        path: "/change-password",
        name: "userPasswordChange",
        component: UserPasswordChange,
      },
      {
        path: "settings",
        name: "settings",
        component: PortalSettings,
        meta: {
          pageTitle: "Settings",
          icon: IconSettings,
        },
      },
      {
        path: "users",
        children: [
          {
            path: "",
            name: "userIndex",
            component: UsersIndex,
            meta: {
              pageTitle: "Users",
              icon: IconUsers,
            },
          },
          {
            path: "new",
            name: "userCreate",
            component: UserCreate,
            meta: {
              pageTitle: "Add New User",
            },
          },
          {
            path: ":userId",
            name: "userEdit",
            component: UserEdit,
            meta: {
              pageTitle: "Edit User",
            },
            props: true,
          },
        ],
      },
      {
        path: "reports",
        name: "reportRoot",
        redirect: {
          name: "reportsOverview",
        },
        meta: {
          pageTitle: "Reports",
        },
        component: ReportsLayout,
        children: [
          {
            path: "",
            name: "reportsOverview",
            component: ReportsOverview,
            meta: {
              pageTitle: "Sales Overview",
              icon: IconSalesOverview,
            },
          },
          {
            path: "gratuity",
            name: "reportsGratuity",
            component: ReportsGratuity,
            meta: {
              pageTitle: "Gratuity Report",
              icon: IconGratuityReport,
            },
          },
          {
            path: "transactions",
            name: "reportsTransactions",
            component: ReportsTransactions,
            meta: {
              pageTitle: "Transactions Report",
              icon: IconTransactionReport,
            },
          },
          {
            path: "product-sales",
            name: "reportsProductSales",
            component: ReportsProductSales,
            meta: {
              pageTitle: "Product Sales Report",
              icon: IconProductSalesReport,
            },
          },
          {
            path: "top-products",
            name: "reportsTopProducts",
            component: ReportsTopProducts,
            meta: {
              pageTitle: "Top Products Report",
              icon: IconTopProductsReport,
            },
          },
          {
            path: ":pathMatch(.*)*",
            redirect: {
              name: "reportsTransactions",
            },
          },
        ],
      },
      {
        path: "history",
        name: "historyRoot",
        redirect: {
          name: "orderIndex",
        },
        meta: {
          pageTitle: "History",
        },
        component: HistoryLayout,
        children: [
          {
            path: "orders",
            name: "orderIndex",
            component: OrdersIndex,
            meta: {
              pageTitle: "Sales History",
              icon: IconOrdersIndex,
            },
          },
          {
            path: "orders/:orderId",
            name: "orderDetail",
            component: OrderDetail,
            props: true,
            meta: {
              pageTitle: "Sale Detail",
            },
          },
          {
            path: "donations/:donationId",
            name: "donationDetail",
            component: DonationDetail,
            props: true,
            meta: {
              pageTitle: "Donation Detail",
            },
          },
          {
            path: "z-reports",
            name: "zReportIndex",
            component: ZReportsIndex,
            meta: {
              pageTitle: "Z Report History",
              icon: IconZReportsIndex,
            },
          },
          {
            path: "z-reports/:zReportId",
            name: "zReportDetail",
            component: ZReportDetail,
            props: true,
            meta: {
              pageTitle: "Z Report Detail",
            },
          },
          {
            path: "transactions",
            name: "transactionIndex",
            component: TransactionsIndex,
            meta: {
              pageTitle: "Transaction History",
              icon: IconTransactionsIndex,
            },
          },
          {
            path: "transactions/:transactionId",
            name: "transactionDetail",
            component: TransactionDetail,
            props: true,
            meta: {
              pageTitle: "Transaction Detail",
            },
          },
        ],
      },
      {
        path: "manage",
        name: "manageRoot",
        redirect: {
          name: "productIndex",
        },
        meta: {
          pageTitle: "Manage",
        },
        children: [
          {
            path: "products",
            children: [
              {
                path: "",
                name: "productIndex",
                component: ProductsIndex,
                meta: {
                  pageTitle: "Manage Products",
                  icon: IconProductsIndex,
                },
              },
              {
                path: ":productId",
                name: "productEdit",
                component: ProductEdit,
                props: true,
                meta: {
                  pageTitle: "Edit Product",
                },
              },
              {
                path: "new",
                name: "productCreate",
                component: ProductCreate,
                meta: {
                  pageTitle: "Add Product",
                },
              },
            ],
          },
          {
            path: "categories",
            children: [
              {
                path: "",
                name: "categoryIndex",
                component: CategoriesIndex,
                meta: {
                  pageTitle: "Manage Categories",
                  icon: IconCategoriesIndex,
                },
              },
              {
                path: ":categoryId",
                name: "categoryEdit",
                component: CategoryEdit,
                props: true,
                meta: {
                  pageTitle: "Edit Category",
                },
              },
              {
                path: "new",
                name: "categoryCreate",
                component: CategoryCreate,
                meta: {
                  pageTitle: "Add Category",
                },
              },
            ],
          },
          {
            path: "imports",
            children: [
              {
                path: "",
                name: "importIndex",
                component: ImportIndex,
                meta: {
                  pageTitle: "Import/Export",
                  icon: IconImportIndex,
                },
              },
              {
                path: ":importId",
                name: "importDetail",
                component: ImportDetail,
                props: true,
                meta: {
                  pageTitle: "Review Import",
                },
              },
            ],
          },
          {
            path: "cashiers",
            children: [
              {
                path: "",
                name: "cashierIndex",
                component: CashiersIndex,
                meta: {
                  pageTitle: "Manage Cashiers",
                  icon: IconCashiersIndex,
                },
              },
              {
                path: ":cashierId",
                name: "cashierEdit",
                component: CashierEdit,
                props: true,
                meta: {
                  pageTitle: "Edit Cashier",
                },
              },
              {
                path: "new",
                name: "cashierCreate",
                component: CashierCreate,
                meta: {
                  pageTitle: "Add Cashier",
                },
              },
            ],
          },

          {
            path: ":pathMatch(.*)*",
            redirect: {
              name: "productIndex",
            },
          },
        ],
      },
    ],
  },
  {
    path: "/reset-password-request/:userType",
    name: "resetPasswordRequest",
    component: PasswordResetRequest,
    props: true,
  },
  {
    path: "/login",
    name: "login",
    component: PortalLogIn,
    meta: {
      pageTitle: "Log In",
    },
    beforeEnter: () => {
      return isLoggedIn.value
        ? {
            name: "dashboard",
          }
        : true
    },
  },
  {
    path: "/merchant-group/login",
    name: "loginMerchantGroup",
    component: MerchantGroupLogin,
    meta: {
      pageTitle: "Log In",
    },
    beforeEnter: () => {
      return isLoggedInMerchantGroup.value
        ? {
            name: "merchantGroupDashboard",
          }
        : true
    },
  },
  {
    path: "/handle-login",
    name: "handleLoginLink",
    component: PortalHandleLoginLink,
  },
  {
    path: "/handle-merchant-group-login",
    name: "handleMerchantGroupLoginLink",
    component: PortalHandleMerchantGroupLoginLink,
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: {
      name: "dashboard",
    },
  },
  {
    path: "/reset-password",
    name: "resetPassword",
    component: UserResetPassword,
  },
  {
    path: "/group-user-reset-password",
    name: "groupUserResetPassword",
    component: MerchantGroupUserResetPassword,
  },
]
const router = createRouter({
  history: createWebHistory(),
  routes: routes,
})

router.beforeEach((to) => {
  setPageTitle(to.meta.pageTitle || "")

  if (to.meta.auth && !isLoggedIn.value) {
    return {
      name: "login",
    }
  }

  if (
    to.meta.merchantGroupAuth &&
    !isLoggedInMerchantGroup.value
  ) {
    return {
      name: "loginMerchantGroup",
    }
  }

  return true
})

export default router
